import { Injectable, OnDestroy, inject } from '@angular/core';
import { CommonIP, DEFAULT_IP_STATE } from '@model/ftf-api.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ApiService } from '@service/api.service';
import { HistoryService } from '@service/history.service';
import { selectFavoriteItems } from '@store/selectors/favorites.selectors';
import { from, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, defaultIfEmpty, withLatestFrom } from 'rxjs/operators';

import * as ItemsActions from '../actions/items.actions';

@Injectable()
export class ItemsEffects implements OnDestroy {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private store = inject(Store);
  private historyService = inject(HistoryService);

  private $ngDestroyed = new Subject<void>();

  ngOnDestroy(): void {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  addItemLite$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemsActions.addItemLite),
      withLatestFrom(this.store.select(selectFavoriteItems)),
      mergeMap(([action, favoriteItems]) =>
        this.apiService.findIP(action.item.identifier, action.item.ipType, action.skipReference, action.skipWorkflow, true).pipe(
          map((item) => {
            const baseState = item.state ?? DEFAULT_IP_STATE;
            let newState;

            if (action.trifoldFilters && action.trifoldFilters.docSubtypeId && action.trifoldFilters.docSubtypeId.length > 0) {
              newState = {
                ...baseState,
                trifoldFilters: action.trifoldFilters,
              };
            } else {
              // Keep existing state or use default without modifying trifoldFilters
              newState = { ...baseState };
            }

            const mutableItem: CommonIP = {
              ...item,
              state: newState,
              favorite: favoriteItems.some((fav) => fav.identifier === item.identifier && fav.ipType === item.ipType),
            };

            this.historyService.saveSearchHistory(mutableItem);

            // return success action
            return ItemsActions.addItemSuccess({ item: mutableItem });
          }),
          catchError((error) => of(ItemsActions.addItemFailure({ error })))
        )
      )
    );
  });

  refreshItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemsActions.refreshItems),
      withLatestFrom(this.store.select(selectFavoriteItems)),
      mergeMap(([action, favoriteItems]) =>
        from(action.refreshItems).pipe(
          mergeMap((itemLite) =>
            this.apiService.findIP(itemLite.identifier, itemLite.ipType, action.skipReference, action.skipWorkflow, true).pipe(
              map((item) => {
                item.favorite = favoriteItems.some((fav) => fav.identifier === item.identifier && fav.ipType === item.ipType);
                return ItemsActions.updateItem({ item, skipState: true });
              }),
              catchError((error) => of(ItemsActions.loadItemsFailure({ error })))
            )
          )
        )
      )
    );
  });

  updateDocStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemsActions.updateDocStatus),
      mergeMap((action) => {
        const obs = this.apiService.updateFileStatus(action.item.identifier, action.item.ipType, action.id, action.action);
        return obs.pipe(
          mergeMap((doc) => {
            const { data } = doc;
            doc.data = undefined;
            const updateAction = ItemsActions.updateItemTrifoldDocument({ item: action.item, id: action.id, doc });
            if (action.action === 'checkout' && data) {
              // checkout triggers two actions: one to update the item in UI and one to download the file
              return [updateAction, ItemsActions.downloadItemDoc({ fileName: doc.fileName!, data })];
            }
            return [updateAction];
          }),
          catchError((error) => of(ItemsActions.loadItemsFailure({ error })))
        );
      })
    );
  });

  deleteItemDoc$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemsActions.deleteItemDoc),
      mergeMap((action) => {
        return this.apiService.deleteFile(action.item.identifier, action.item.ipType, action.id).pipe(
          defaultIfEmpty(null),
          map(() =>
            ItemsActions.refreshItems({
              refreshItems: [action.item],
              skipReference: true,
              skipWorkflow: true,
            })
          ),
          catchError((error) => {
            return of(ItemsActions.loadItemsFailure({ error }));
          })
        );
      })
    );
  });
}
